import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
  showCategory?: boolean;
}

export default function ArticleCard({
  article,
  variant = 'default',
  showCategory = true,
}: ArticleCardProps) {
  if (variant === 'horizontal') {
    return (
      <article
        className="group flex gap-3 md:gap-4 p-3 rounded-lg hover:bg-background-100/80 transition-colors"
        itemScope
        itemType="https://schema.org/Article"
      >
        <Link
          to={`/article/${article.slug}`}
          className="flex-shrink-0 w-24 h-16 md:w-32 md:h-20 rounded-md overflow-hidden"
        >
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            itemProp="image"
          />
        </Link>
        <div className="flex-1 min-w-0">
          <Link to={`/article/${article.slug}`}>
            <h3
              className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 line-clamp-2 transition-colors"
              itemProp="headline"
            >
              {article.isHot && (
                <span className="inline-flex items-center mr-1.5">
                  <i className="ri-fire-fill text-accent-500 text-xs" />
                </span>
              )}
              {article.isExclusive && (
                <span className="inline-flex items-center px-1.5 py-0.5 bg-accent-100 text-accent-700 text-[10px] font-semibold rounded mr-1.5 align-middle">
                  独家
                </span>
              )}
              {article.title}
            </h3>
          </Link>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-400 flex-wrap">
            {showCategory && (
              <span className="text-primary-600" itemProp="articleSection">
                {article.categoryName}
              </span>
            )}
            <span>{article.publishDate}</span>
            <span className="flex items-center gap-0.5">
              <i className="ri-eye-line text-xs" />
              {article.views > 10000
                ? `${(article.views / 10000).toFixed(1)}万`
                : article.views}
            </span>
          </div>
        </div>
        <meta itemProp="datePublished" content={article.publishDate} />
        <meta itemProp="author" content={article.author || '黑料吃瓜网'} />
      </article>
    );
  }

  if (variant === 'compact') {
    return (
      <article className="group py-2.5 border-b border-background-200/50 last:border-0">
        <Link to={`/article/${article.slug}`} className="flex items-start gap-2">
          <span className="flex-shrink-0 w-5 h-5 bg-primary-50 text-primary-600 text-xs font-bold rounded flex items-center justify-center mt-0.5">
            热
          </span>
          <h3 className="text-sm text-foreground-800 group-hover:text-primary-600 line-clamp-2 transition-colors">
            {article.title}
          </h3>
        </Link>
        <div className="flex items-center gap-2 mt-1 pl-7 text-xs text-foreground-400">
          <span>{article.categoryName}</span>
          <span>{article.publishDate}</span>
        </div>
      </article>
    );
  }

  // Default card
  return (
    <article
      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200/50 transition-all duration-300"
      itemScope
      itemType="https://schema.org/Article"
    >
      <Link to={`/article/${article.slug}`} className="block">
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            itemProp="image"
          />
          {(article.isHot || article.isExclusive) && (
            <div className="absolute top-3 left-3 flex gap-1.5">
              {article.isExclusive && (
                <span className="px-2 py-1 bg-accent-500 text-background-50 text-xs font-semibold rounded-md">
                  <i className="ri-vip-diamond-line mr-1" />
                  独家
                </span>
              )}
              {article.isHot && (
                <span className="px-2 py-1 bg-primary-500 text-background-50 text-xs font-semibold rounded-md">
                  <i className="ri-fire-line mr-1" />
                  热门
                </span>
              )}
            </div>
          )}
        </div>
      </Link>
      <div className="p-4 md:p-5">
        {showCategory && (
          <div className="flex items-center gap-2 mb-2">
            <span
              className="text-xs font-medium text-primary-600"
              itemProp="articleSection"
            >
              {article.categoryName}
            </span>
          </div>
        )}
        <Link to={`/article/${article.slug}`}>
          <h3
            className="text-base font-medium text-foreground-900 group-hover:text-primary-600 line-clamp-2 transition-colors mb-2"
            itemProp="headline"
          >
            {article.title}
          </h3>
        </Link>
        <p className="text-sm text-foreground-500 line-clamp-2 mb-3" itemProp="description">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" />
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {article.views > 10000
                ? `${(article.views / 10000).toFixed(1)}万`
                : article.views}
            </span>
          </div>
          <span className="flex items-center gap-1">
            <i className="ri-message-3-line" />
            {article.comments || 0}
          </span>
        </div>
      </div>
      <meta itemProp="datePublished" content={article.publishDate} />
      <meta itemProp="author" content={article.author || '黑料吃瓜网'} />
    </article>
  );
}